import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { ROLES } from 'app/shared/enums/roles';
import { ClientService } from 'app/services/client.service';
import { DistributorService } from 'app/services/distributor.service';

@Injectable()
export class AuthService
{
    private _authenticated: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private clientsService: ClientService,
        private distributorService: DistributorService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string)
    {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string
    {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any>
    {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any>
    {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any>
    {
        // Throw error, if the user is already logged in
        if ( this._authenticated )
        {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post(`${environment.apiUrl}/auth/login`, credentials).pipe(
            switchMap((response: any) => {

                // Store the access token in the local storage
                this.accessToken = response.data.accessToken.token;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                const user = response.data.user;

                this._userService.user = {
                    ...user,
                    name: user.username,
                    tabs: response.data.tabs
                }

                if (+response.data.user.role_id == ROLES.CLIENTE){
                    this.clientsService.checkClient().subscribe({
                        next: (response) => {
                            localStorage.setItem('currentClient', JSON.stringify(response.data));
                        },
                        error: (err: any) => {},
                        complete: () => {}});
                }
                if (+response.data.user.role_id == ROLES.DISTRIBUIDOR){
                    this.distributorService.checkDistributor().subscribe({
                        next: (response) => {
                            localStorage.setItem('currentDistributor', JSON.stringify(response.data));
                        },
                        error: (err: any) => {},
                        complete: () => {}});
                }
                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any>
    {
        // Sign in using the token
        return this._httpClient.get(`${environment.apiUrl}/auth/user`).pipe(
            switchMap((response: any) => {

                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                
                localStorage.setItem('currentUser', JSON.stringify(response.data));
                if ( response.data.accessToken )
                {
                    this.accessToken = response.data.accessToken.token;
                }
                if (+response.data.user.role_id == ROLES.CLIENTE){
                    this.clientsService.checkClient().subscribe({
                        next: (response) => {
                            localStorage.setItem('currentClient', JSON.stringify(response.data));
                        },
                        error: (err: any) => {},
                        complete: () => {}});
                }
                if (+response.data.user.role_id == ROLES.DISTRIBUIDOR){
                    this.distributorService.checkDistributor().subscribe({
                        next: (response) => {
                            localStorage.setItem('currentDistributor', JSON.stringify(response.data));
                        },
                        error: (err: any) => {},
                        complete: () => {}});
                }
                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                const user = response.data.user;

                this._userService.user = {
                    ...user,
                    name: user.username,
                    tabs: response.data.tabs
                }

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if ( this._authenticated )
        {
            return of(true);
        }

        // Check the access token availability
        if ( !this.accessToken )
        {
            return of(false);
        }

        // Temporal
        // return of(true)

        // Check the access token expire date
        // if ( AuthUtils.isTokenExpired(this.accessToken) )
        // {
        //     return of(false);
        // }

        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
